import React from "react"
import { graphql } from "gatsby"
import groupBy from "lodash/groupBy"
import sortBy from "lodash/sortBy"
import { withPrismicPreview } from "gatsby-plugin-prismic-previews"
import { useTranslation } from "gatsby-plugin-react-i18next"

import { linkResolver } from "utils"

import { AppDownloadPromo } from '../../app-download-promo/AppDownloadPromo'
import { CenterHours } from '../../location/center-hours/CenterHours'
import { CenterInfo } from '../../location/center-info/CenterInfo'
import { Hero } from '../../location/hero/Hero'
import { Layout } from '../../layout/Layout'
import { LocalitiesServedByLocation } from '../../location/localities-served-by-location/LocalitiesServedByLocation'
import { LocationFaq } from '../../location/faq/Faq'
import { Reviews } from '../../location/reviews/Reviews'
import { Seo } from '../../seo/Seo'
import { ThreeColumn } from '../../three-column/ThreeColumn'

import { convertToTwentyFourHourTime } from "./convertToTwentyFourHourTime"

type LocationPageProps = {
  pageContext: {
    amenities: string[];
    centerHours: {
      monday: {
        opensAt: string;
        closesAt: string;
      },
      tuesday: {
        opensAt: string;
        closesAt: string;
      },
      wednesday: {
        opensAt: string;
        closesAt: string;
      },
      thursday: {
        opensAt: string;
        closesAt: string;
      },
      friday: {
        opensAt: string;
        closesAt: string;
      },
      saturday: {
        opensAt: string;
        closesAt: string;
      },
      sunday: {
        opensAt: string;
        closesAt: string;
      }
    };
    cityRegionPostal: string;
    comingSoon: boolean;
    directionsLink: string;
    googleMapEmbedLink: string;
    localities: {
      city: string;
      state: string;
    }[];
    location: string;
    newDonorBonusAmount: string;
    reviews: {
      review_author: string;
      review_body: string;
    }[];
    streetAddress: string;
    yearOpening: string;
  }
}

type OperatingHoursSpecProps = {
  day: string;
  openingHours: string;
  closingHours: string;
}[] | undefined

const LocationPage: React.FC<LocationPageProps> = ( props ) => {
  const context = props.pageContext
  const {
    amenities,
    centerHours,
    cityRegionPostal,
    comingSoon,
    directionsLink,
    googleMapEmbedLink,
    localities,
    location,
    newDonorBonusAmount,
    reviews,
    streetAddress,
    yearOpening
  } = context
  const { t } = useTranslation("shared", {
    keyPrefix: "location_pages.location",
  });
  const defaultBonusAmount = "$200"
  const bonusAmount = newDonorBonusAmount || defaultBonusAmount;
  const heroIntro = t("intro", { bonusAmount });
  let heroComingSoon = "";
  if ( comingSoon ) {
    heroComingSoon = yearOpening ? `(${t("coming_soon_with_year", { yearOpening })})` : `(${t("coming_soon")})`;
  }

  const sortedLocalities =  sortBy(localities, ["state", "city"])
  const sortedLocalitiesWithNoDuplicates = sortedLocalities.filter((item, index, array) => {
    const previousItem = index - 1;
    if (index === 0 || (item.city !== array[previousItem].city)) {
      return true;
    }
  });
  const regionSortedLocalities = Object.entries(groupBy(sortedLocalitiesWithNoDuplicates, "state"))

  return (
    <Layout>
      <Hero heroComingSoon={heroComingSoon} intro={heroIntro} title={location} />
      <CenterInfo 
        amenities={amenities}
        cityRegionPostal={cityRegionPostal}
        directionsLink={directionsLink}
        googleMapEmbedLink={googleMapEmbedLink}
        streetAddress={streetAddress}
      />
      {centerHours && !comingSoon && (
        <CenterHours centerHours={centerHours} />
      )}
      {reviews?.length > 0 && !comingSoon && (<Reviews reviews={reviews} />)}
      <ThreeColumn />
      <LocationFaq />
      <AppDownloadPromo />
      {regionSortedLocalities && (<LocalitiesServedByLocation location={location} supportedLocalities={regionSortedLocalities} />)}
    </Layout>
  )
}

export const Head = pageData => {
  const { t } = useTranslation("shared", {
    keyPrefix: "location_pages.location",
  });

  const {centerHours, city, ogDescription, ogImage, ogTitle, postalCode, state, streetAddress, url } =
    pageData?.pageContext
  const canonicalUrl = `https://www.joinparachute.com${url}`
  const description = ogDescription || t("description", { city, state })

  const operatingHoursArray: OperatingHoursSpecProps  = Object.entries(centerHours).map((item) => {
    const day = item[0];
    const capitalizedDay = day[0].toUpperCase() + day.slice(1);
    const {
      opensAt,
      closesAt
    } = item[1]
    if((opensAt && opensAt !== "Closed") || ( closesAt && closesAt !== "Closed") ) {
      const openingHours = convertToTwentyFourHourTime(opensAt);
      const closingHours = convertToTwentyFourHourTime(closesAt)
      return {
        day: capitalizedDay,
        openingHours,
        closingHours
      }
    }
  });

  const operatingHours = operatingHoursArray?.filter( Boolean );

  const operatingHoursSpec = operatingHours?.map((item) => {
    const {
      day,
      openingHours,
      closingHours
    } = item;
    if( day || openingHours || closingHours ) {
      return {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": day,
        "opens": openingHours,
        "closes": closingHours
      }
    }
  });

  const jsonLD = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    address: {
      addressLocality: city,
      addressRegion: state,
      postalCode,
      streetAddress,
    },
    openingHoursSpecification: operatingHoursSpec,
    url: canonicalUrl,
  }

  return (
    <Seo
      canonicalUrl={canonicalUrl}
      jsonLD={JSON.stringify(jsonLD)}
      ogDescription={description}
      ogImage={ogImage?.url}
      ogTitle={ogTitle}
      ogType="page"
      title={`${city}, ${state} | Plasma Donation Center`}
    />
  )
}

export default withPrismicPreview(LocationPage, [
  {
    repositoryName: process.env.GATSBY_PRISMIC_REPO_NAME,
    linkResolver,
  },
])

export const locationPageQuery = graphql`
  query ($language: String! = "en") {
    locales: allLocale(
      filter: { ns: { in: ["shared"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
